import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Sliders, 
  X, 
  Sparkles, 
  Check, 
  RotateCcw, 
  GripVertical, 
  Eye, 
  Layers, 
  Spline, 
  CornerDownRight, 
  ArrowRight,
  Gauge
} from 'lucide-react';
import { VdpElement } from '../../types';
import { SimplifyOptions, simplifyElements } from '../../utils/simplifyPathUtils';
import { SimplifyPathIcon } from './SimplifyPathIcon';

export interface SimplifyPathModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedElements: VdpElement[];
  onApply: (
    updatedElements: VdpElement[], 
    summary: { totalOriginal: number; totalSimplified: number; overallReduction: number }
  ) => void;
  onPreviewUpdate?: (previewElements: VdpElement[] | null) => void;
}

export const SimplifyPathModal: React.FC<SimplifyPathModalProps> = ({
  isOpen,
  onClose,
  selectedElements,
  onApply,
  onPreviewUpdate,
}) => {
  const [tolerance, setTolerance] = useState<number>(2.5);
  const [cornerThreshold, setCornerThreshold] = useState<number>(45);
  const [smoothCurves, setSmoothCurves] = useState<boolean>(true);
  const [livePreview, setLivePreview] = useState<boolean>(true);
  const [selectedPreset, setSelectedPreset] = useState<'subtle' | 'balanced' | 'aggressive' | 'lowpoly' | 'custom'>('balanced');

  // Draggable window state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Filter only vectorizable elements (pen, path, shapes)
  const vectorTargets = useMemo(() => {
    return selectedElements.filter(el => 
      el.type === 'pen' || 
      el.type === 'path' || 
      Boolean(el.points && el.points.length > 0) || 
      Boolean(el.pathData || el.pathD) ||
      ['star', 'polygon', 'rectangle', 'circle', 'triangle', 'diamond', 'heart', 'arrow'].includes(el.type)
    );
  }, [selectedElements]);

  // Compute live simplification stats and geometry
  const options: SimplifyOptions = useMemo(() => ({
    tolerance,
    cornerThresholdDegrees: cornerThreshold,
    smoothCurves,
  }), [tolerance, cornerThreshold, smoothCurves]);

  const { updatedElements, totalOriginal, totalSimplified, overallReduction } = useMemo(() => {
    if (vectorTargets.length === 0) {
      return { updatedElements: [], totalOriginal: 0, totalSimplified: 0, overallReduction: 0 };
    }
    return simplifyElements(vectorTargets, options);
  }, [vectorTargets, options]);

  const onPreviewUpdateRef = useRef(onPreviewUpdate);
  useEffect(() => {
    onPreviewUpdateRef.current = onPreviewUpdate;
  });

  // Sync canvas live preview
  useEffect(() => {
    if (!isOpen) {
      onPreviewUpdateRef.current?.(null);
      return;
    }
    if (livePreview && updatedElements.length > 0) {
      onPreviewUpdateRef.current?.(updatedElements);
    } else {
      onPreviewUpdateRef.current?.(null);
    }
  }, [isOpen, livePreview, updatedElements]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      onPreviewUpdateRef.current?.(null);
    };
  }, []);

  if (!isOpen) return null;

  const handlePointerDownDrag = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!modalRef.current) return;

    const rect = modalRef.current.getBoundingClientRect();
    const currX = position ? position.x : rect.left;
    const currY = position ? position.y : rect.top;

    if (!position) {
      setPosition({ x: rect.left, y: rect.top });
    }

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: currX,
      initialY: currY,
    };

    const handlePointerMove = (moveEvt: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = moveEvt.clientX - dragRef.current.startX;
      const dy = moveEvt.clientY - dragRef.current.startY;
      setPosition({
        x: Math.max(10, Math.min(window.innerWidth - 380, dragRef.current.initialX + dx)),
        y: Math.max(10, Math.min(window.innerHeight - 440, dragRef.current.initialY + dy)),
      });
    };

    const handlePointerUp = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const applyPreset = (preset: 'subtle' | 'balanced' | 'aggressive' | 'lowpoly') => {
    setSelectedPreset(preset);
    if (preset === 'subtle') {
      setTolerance(1.0);
      setCornerThreshold(40);
      setSmoothCurves(true);
    } else if (preset === 'balanced') {
      setTolerance(2.5);
      setCornerThreshold(45);
      setSmoothCurves(true);
    } else if (preset === 'aggressive') {
      setTolerance(6.0);
      setCornerThreshold(55);
      setSmoothCurves(true);
    } else if (preset === 'lowpoly') {
      setTolerance(10.0);
      setCornerThreshold(30);
      setSmoothCurves(false);
    }
  };

  const handleCommit = () => {
    if (updatedElements.length === 0) {
      onClose();
      return;
    }
    onPreviewUpdate?.(null);
    onApply(updatedElements, { totalOriginal, totalSimplified, overallReduction });
    onClose();
  };

  const handleCancel = () => {
    onPreviewUpdate?.(null);
    onClose();
  };

  const handleReset = () => {
    applyPreset('balanced');
  };

  return (
    <div
      ref={modalRef}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={
        position
          ? {
              position: 'fixed',
              left: `${position.x}px`,
              top: `${position.y}px`,
              zIndex: 99999,
            }
          : {
              position: 'fixed',
              right: '32px',
              top: '110px',
              zIndex: 99999,
            }
      }
      className="w-96 bg-zinc-900/95 backdrop-blur-2xl border border-zinc-700/80 rounded-2xl shadow-2xl p-4.5 text-zinc-100 font-sans select-none pointer-events-auto animate-in fade-in zoom-in-95 duration-150"
    >
      {/* Title Bar & Drag Handle */}
      <div className="flex items-center justify-between pb-3 border-b border-zinc-800 mb-3.5">
        <div 
          onPointerDown={handlePointerDownDrag}
          className="flex items-center gap-2 cursor-grab active:cursor-grabbing text-zinc-300 hover:text-white transition-colors flex-1"
        >
          <GripVertical className="w-4 h-4 text-zinc-500" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center">
              <SimplifyPathIcon className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm leading-tight">Simplify Vector Path</h3>
              <p className="text-[10px] text-zinc-400">Reduce anchor points & eliminate jitter</p>
            </div>
          </div>
        </div>
        <button
          onClick={handleCancel}
          className="p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer"
          title="Close without saving"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Target Status Banner */}
      {vectorTargets.length === 0 ? (
        <div className="mb-4 p-3 rounded-xl bg-amber-950/40 border border-amber-500/30 text-amber-300 text-xs flex items-center gap-2">
          <Layers className="w-4 h-4 shrink-0 text-amber-400" />
          <span>Select at least 1 vector path, pencil stroke, or shape to simplify.</span>
        </div>
      ) : (
        <div className="mb-4 p-3 rounded-xl bg-indigo-950/40 border border-indigo-500/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-[10px] uppercase font-bold text-zinc-400">Original</div>
              <div className="text-sm font-mono font-bold text-zinc-200">{totalOriginal} pts</div>
            </div>
            <ArrowRight className="w-4 h-4 text-indigo-400" />
            <div className="text-center">
              <div className="text-[10px] uppercase font-bold text-emerald-400">Simplified</div>
              <div className="text-sm font-mono font-bold text-emerald-300">{totalSimplified} pts</div>
            </div>
          </div>

          <div className="flex flex-col items-end">
            <span className="text-[9px] uppercase font-bold text-zinc-400">Reduction</span>
            <span className="bg-emerald-500 text-black font-extrabold text-xs px-2 py-0.5 rounded-full shadow-xs">
              -{overallReduction}%
            </span>
          </div>
        </div>
      )}

      {/* Quick Presets */}
      <div className="mb-3.5">
        <label className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5 block">
          Optimization Presets
        </label>
        <div className="grid grid-cols-4 gap-1.5">
          <button
            type="button"
            onClick={() => applyPreset('subtle')}
            className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer text-center ${
              selectedPreset === 'subtle'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-zinc-800/70 text-zinc-300 hover:bg-zinc-800 hover:text-white'
            }`}
          >
            Subtle
          </button>
          <button
            type="button"
            onClick={() => applyPreset('balanced')}
            className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer text-center ${
              selectedPreset === 'balanced'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-zinc-800/70 text-zinc-300 hover:bg-zinc-800 hover:text-white'
            }`}
          >
            Balanced
          </button>
          <button
            type="button"
            onClick={() => applyPreset('aggressive')}
            className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer text-center ${
              selectedPreset === 'aggressive'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-zinc-800/70 text-zinc-300 hover:bg-zinc-800 hover:text-white'
            }`}
          >
            Aggressive
          </button>
          <button
            type="button"
            onClick={() => applyPreset('lowpoly')}
            className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer text-center ${
              selectedPreset === 'lowpoly'
                ? 'bg-indigo-600 text-white shadow-xs font-semibold'
                : 'bg-zinc-800/70 text-zinc-300 hover:bg-zinc-800 hover:text-white'
            }`}
          >
            Angular
          </button>
        </div>
      </div>

      {/* Parameter Controls */}
      <div className="space-y-3.5 mb-4">
        {/* Tolerance Slider */}
        <div className="bg-zinc-800/40 p-2.5 rounded-xl border border-zinc-800">
          <div className="flex justify-between items-center mb-1 text-xs">
            <span className="text-zinc-300 font-medium flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5 text-indigo-400" />
              Tolerance / Deviation:
            </span>
            <span className="font-mono text-indigo-300 font-semibold">{tolerance.toFixed(1)} px</span>
          </div>
          <input
            type="range"
            min="0.5"
            max="15.0"
            step="0.5"
            value={tolerance}
            onChange={(e) => {
              setTolerance(parseFloat(e.target.value));
              setSelectedPreset('custom');
            }}
            className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
          />
          <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
            <span>Fine Detail (0.5px)</span>
            <span>Balanced</span>
            <span>Minimal Points (15px)</span>
          </div>
        </div>

        {/* Corner Threshold Slider */}
        <div className="bg-zinc-800/40 p-2.5 rounded-xl border border-zinc-800">
          <div className="flex justify-between items-center mb-1 text-xs">
            <span className="text-zinc-300 font-medium flex items-center gap-1.5">
              <CornerDownRight className="w-3.5 h-3.5 text-amber-400" />
              Corner Angle Threshold:
            </span>
            <span className="font-mono text-amber-300 font-semibold">{cornerThreshold}°</span>
          </div>
          <input
            type="range"
            min="10"
            max="120"
            step="5"
            value={cornerThreshold}
            onChange={(e) => {
              setCornerThreshold(parseInt(e.target.value, 10));
              setSelectedPreset('custom');
            }}
            className="w-full h-1.5 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
          />
          <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
            <span>Keep subtle corners (10°)</span>
            <span>Standard (45°)</span>
            <span>Only sharp corners (120°)</span>
          </div>
        </div>

        {/* Checkbox Options */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/40 border border-zinc-800 hover:bg-zinc-800/70 transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={smoothCurves}
              onChange={(e) => {
                setSmoothCurves(e.target.checked);
                setSelectedPreset('custom');
              }}
              className="rounded accent-indigo-500"
            />
            <span className="text-zinc-300 select-none flex items-center gap-1.5">
              <Spline className="w-3.5 h-3.5 text-cyan-400" />
              Bezier Curves
            </span>
          </label>

          <label className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/40 border border-zinc-800 hover:bg-zinc-800/70 transition-colors cursor-pointer">
            <input
              type="checkbox"
              checked={livePreview}
              onChange={(e) => setLivePreview(e.target.checked)}
              className="rounded accent-indigo-500"
            />
            <span className="text-zinc-300 select-none flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5 text-emerald-400" />
              Live Preview
            </span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
        <button
          type="button"
          onClick={handleReset}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors cursor-pointer"
          title="Reset sliders to default balanced settings"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>Reset</span>
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={vectorTargets.length === 0}
            onClick={handleCommit}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white text-xs font-semibold shadow-md shadow-indigo-600/30 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Check className="w-3.5 h-3.5" />
            <span>Apply Simplify</span>
          </button>
        </div>
      </div>
    </div>
  );
};
